import { useState, useEffect, Fragment } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useSession } from "@/context/SessionContext";
import { RagChip } from "@/components/RagChip";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Download, Search, ChevronLeft, ChevronRight, Loader2, ChevronDown, ChevronUp, FileText, X,
} from "lucide-react";
import { toast } from "sonner";

export default function MonthlyReview() {
  const { session, asOf } = useSession();
  const isAdmin = session?.role === "pmo_admin";
  const [search, setSearch] = useState("");
  const [platform, setPlatform] = useState("all");
  const [health, setHealth] = useState("all");
  const [batch, setBatch] = useState("all");
  const [superseded, setSuperseded] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [expanded, setExpanded] = useState(null);

  const { data: filters } = useQuery({
    queryKey: ["filters", session?.persona],
    queryFn: async () => (await api.get("/filters")).data,
  });
  const { data: runs } = useQuery({
    queryKey: ["runs", session?.persona],
    queryFn: async () => (await api.get("/runs")).data,
    enabled: isAdmin,
  });

  const effMonth = asOf === "latest" ? (filters?.months?.[0]?.month_key ?? "") : asOf;
  useEffect(() => { setPage(1); }, [asOf]);

  const params = {
    month: effMonth || undefined, all_months: false, search, platform, health,
    batch: isAdmin && batch !== "all" ? batch : undefined,
    include_superseded: isAdmin && superseded, page, page_size: pageSize,
  };
  const { data, isLoading } = useQuery({
    queryKey: ["monthly-review", session?.persona, params],
    queryFn: async () => (await api.get("/monthly-review", { params })).data,
  });

  const exportCsv = async () => {
    try {
      const res = await api.get("/monthly-review/export", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "pmo_monthly_updates.csv"; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Monthly updates exported");
    } catch { toast.error("Export failed"); }
  };

  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-4 fluent-enter" data-testid="monthly-review-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-[#201F1E]">Monthly Import Review</h1>
          <p className="text-sm text-[#605E5C] mt-1">Actual monthly submissions. Each row is one project-month update.</p>
        </div>
        <Button onClick={exportCsv} data-testid="monthly-export-button" className="bg-[#0078D4] hover:bg-[#005A9E] text-white h-9 text-sm fluent-focus">
          <Download size={15} className="mr-1.5" /> Export CSV
        </Button>
      </div>

      <div className="fluent-card p-3 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-[#605E5C] whitespace-nowrap" data-testid="review-month-indicator">
          Reporting month:
          <span className="text-[#201F1E]">{filters?.months?.find((m) => m.month_key === effMonth)?.month_label || "—"}</span>
        </div>
        <div className="relative flex-1 min-w-[180px]">
          <Search size={15} className="absolute left-2.5 top-2.5 text-[#605E5C]" />
          <Input data-testid="review-search" placeholder="Search project, contributor, tech…" value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="pl-8 h-9 text-sm fluent-focus" />
        </div>
        <Select value={platform} onValueChange={(v) => { setPlatform(v); setPage(1); }}>
          <SelectTrigger className="h-9 w-[140px] text-sm fluent-focus" data-testid="review-platform"><SelectValue placeholder="Platform" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Platforms</SelectItem>
            {(filters?.platforms || []).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={health} onValueChange={(v) => { setHealth(v); setPage(1); }}>
          <SelectTrigger className="h-9 w-[140px] text-sm fluent-focus" data-testid="review-health"><SelectValue placeholder="Health" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Health</SelectItem>
            <SelectItem value="green">Green</SelectItem>
            <SelectItem value="amber">Amber</SelectItem>
            <SelectItem value="red">Red</SelectItem>
            <SelectItem value="not_rated">Not Rated</SelectItem>
          </SelectContent>
        </Select>
        {isAdmin && (
          <Select value={batch} onValueChange={(v) => { setBatch(v); setPage(1); }}>
            <SelectTrigger className="h-9 w-[200px] text-sm fluent-focus" data-testid="review-batch"><SelectValue placeholder="Import batch" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All batches</SelectItem>
              {(runs?.runs || []).map((r) => (
                <SelectItem key={r.run_id} value={r.run_id}>{r.provenance?.filename} · {(r.months || []).join(", ")}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {isAdmin && (
          <label className="flex items-center gap-1.5 text-xs text-[#323130] cursor-pointer" data-testid="superseded-toggle-label">
            <Checkbox checked={superseded} onCheckedChange={(v) => { setSuperseded(!!v); setPage(1); }} data-testid="superseded-toggle" />
            Include superseded (audit)
          </label>
        )}
      </div>

      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-[#201F1E]" data-testid="count-label">{data?.count_label}</div>
        {data?.all_months && <span className="text-[11px] text-[#605E5C]">Repeated project names across months are expected.</span>}
      </div>

      <div className="fluent-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#F3F2F1] text-left text-xs uppercase tracking-wide text-[#605E5C]">
                <th className="px-3 py-2.5 w-8"></th>
                <th className="px-4 py-2.5 font-semibold">Health</th>
                <th className="px-4 py-2.5 font-semibold">Project</th>
                <th className="px-4 py-2.5 font-semibold">Reporting month</th>
                <th className="px-4 py-2.5 font-semibold">Platform</th>
                <th className="px-4 py-2.5 font-semibold">Completion</th>
                <th className="px-4 py-2.5 font-semibold">Rev</th>
                <th className="px-4 py-2.5 font-semibold">Owner</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={8} className="px-4 py-12 text-center"><Loader2 className="animate-spin inline text-[#0078D4]" /></td></tr>
              ) : (data?.items || []).length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-[#605E5C]">No submissions match your filters.</td></tr>
              ) : data.items.map((row, i) => (
                <Fragment key={row.id}>
                  <tr data-testid={`review-row-${i}`} className={`border-t border-[#EDEBE9] hover:bg-[#F3F9FD] ${!row.active ? "opacity-60" : ""}`}>
                    <td className="px-3 py-3">
                      <button data-testid={`review-expand-${i}`} onClick={() => setExpanded(expanded === row.id ? null : row.id)} className="text-[#605E5C] hover:text-[#0078D4] fluent-focus rounded">
                        {expanded === row.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </button>
                    </td>
                    <td className="px-4 py-3"><RagChip rag={row.rag} /></td>
                    <td className="px-4 py-3"><div className="font-semibold text-[#201F1E]">{row.project_name}</div><div className="text-[11px] text-[#605E5C]">{row.tech_stack}</div></td>
                    <td className="px-4 py-3">{row.month_label}</td>
                    <td className="px-4 py-3">{row.platform}</td>
                    <td className="px-4 py-3">{row.pct_completion ?? "—"}%</td>
                    <td className="px-4 py-3">
                      v{row.revision}{!row.active && <span className="ml-1 text-[10px] text-[#A80000] font-semibold">superseded</span>}
                    </td>
                    <td className="px-4 py-3">{row.contributor_name}</td>
                  </tr>
                  {expanded === row.id && (
                    <tr className="bg-[#FAFAFA]" data-testid={`review-panel-${i}`}>
                      <td colSpan={8} className="px-6 py-4">
                        <div className="grid md:grid-cols-2 gap-x-8 gap-y-3 text-sm">
                          <RField label="Scope / About">{row.about}</RField>
                          <RField label="Bandwidth">{row.bandwidth}</RField>
                          <RField label="Key Achievements">{row.achievements}</RField>
                          <RField label="Challenges">{row.challenges}</RField>
                          <RField label="Dependencies / Blockers">{row.blockers}</RField>
                          <RField label="Support Ask">{row.support_required}</RField>
                          <RField label="Planned Focus Next Month">{row.next_month_focus}</RField>
                          <RField label="Health rationale">{row.rag_reason}</RField>
                        </div>
                        {isAdmin && row.provenance && (
                          <div className="mt-3 rounded bg-white border border-[#E1DFDD] p-3 text-xs text-[#323130]" data-testid={`provenance-${i}`}>
                            <div className="font-semibold text-[#201F1E] mb-1 flex items-center gap-1"><FileText size={13} /> Import provenance</div>
                            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1">
                              <span>File: <b>{row.provenance.filename}</b></span>
                              <span>Uploaded: <b>{row.provenance.uploaded_at}</b></span>
                              <span>Uploader: <b>{row.provenance.uploader}</b></span>
                              <span>State: <b>{row.provenance.state}</b></span>
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-[#EDEBE9]">
          <div className="flex items-center gap-2 text-xs text-[#605E5C]">
            Rows per page
            <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
              <SelectTrigger className="h-8 w-[70px] text-xs" data-testid="review-page-size"><SelectValue /></SelectTrigger>
              <SelectContent>{[10, 25, 50].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-3 text-xs">
            <span data-testid="review-pagination-info">Page {page} of {totalPages}</span>
            <Button variant="outline" size="sm" className="h-8 fluent-focus" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="review-prev"><ChevronLeft size={14} /></Button>
            <Button variant="outline" size="sm" className="h-8 fluent-focus" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="review-next"><ChevronRight size={14} /></Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function RField({ label, children }) {
  const v = children && String(children).trim() ? children : null;
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-[#605E5C]">{label}</div>
      <div className="text-sm text-[#323130] mt-0.5">{v || <span className="text-[#A19F9D] italic">Not reported</span>}</div>
    </div>
  );
}
